import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowUp, ArrowDown, X, CheckCircle2, MapPin, Route } from 'lucide-react';
import CustomerCombobox from './CustomerCombobox';
import NextServiceNote from './NextServiceNote';

const RouteStop = ({ c, index, total, onMove, onRemove, onPerform }) => (
  <Card data-testid={`route-stop-${c.id}`} className={c.done ? 'bg-emerald-50/60 border-emerald-200' : ''}>
    <CardContent className="py-3 flex items-start gap-3">
      <span className="h-7 w-7 shrink-0 rounded-full bg-slate-900 text-white text-sm font-semibold flex items-center justify-center">{index + 1}</span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={`/customers/${c.id}`} className="font-semibold text-slate-900 hover:text-brand-700 underline-offset-2 hover:underline" data-testid={`route-open-customer-${c.id}`}>{c.name}</Link>
          {c.done && <span className="px-2 py-0.5 text-xs rounded-full bg-emerald-100 text-emerald-700" data-testid={`route-stop-done-${c.id}`}>Renset i dag</span>}
        </div>
        <div className="text-xs text-slate-500 mt-0.5">{[c.address, c.postal_code, c.region].filter(Boolean).join(' · ') || '-'}</div>
        {(c.door_code || c.gate_code || c.alarm_code) && (
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {c.door_code && <span className="px-2 py-0.5 text-xs rounded bg-slate-100">Dør: <strong>{c.door_code}</strong></span>}
            {c.gate_code && <span className="px-2 py-0.5 text-xs rounded bg-slate-100">Port: <strong>{c.gate_code}</strong></span>}
            {c.alarm_code && <span className="px-2 py-0.5 text-xs rounded bg-slate-100">Alarm: <strong>{c.alarm_code}</strong></span>}
          </div>
        )}
        {c.next_service_note && <div className="mt-2"><NextServiceNote customerId={c.id} note={c.next_service_note} editable={false} /></div>}
      </div>
      <div className="flex flex-wrap justify-end gap-1 shrink-0 max-w-[132px] sm:max-w-none">
        {c.address && (
          <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([c.address, c.postal_code].filter(Boolean).join(' '))}`} target="_blank" rel="noreferrer" data-testid={`route-map-${c.id}`}>
            <Button size="sm" variant="outline" className="h-10 w-10 sm:h-8 sm:w-8 p-0"><MapPin className="h-4 w-4" /></Button>
          </a>
        )}
        <Button size="sm" variant="outline" className="h-10 w-10 sm:h-8 sm:w-8 p-0" disabled={index === 0} onClick={() => onMove(index, -1)} data-testid={`route-up-${c.id}`}><ArrowUp className="h-4 w-4" /></Button>
        <Button size="sm" variant="outline" className="h-10 w-10 sm:h-8 sm:w-8 p-0" disabled={index === total - 1} onClick={() => onMove(index, 1)} data-testid={`route-down-${c.id}`}><ArrowDown className="h-4 w-4" /></Button>
        <Button size="sm" variant="ghost" className="h-10 w-10 sm:h-8 sm:w-8 p-0 text-red-600" onClick={() => onRemove(c.id)} data-testid={`route-remove-${c.id}`}><X className="h-4 w-4" /></Button>
        {!c.done && (
          <Button size="sm" className="h-10 sm:h-8 bg-brand-600 hover:bg-brand-700 text-white" onClick={(e) => onPerform(c, e)} data-testid={`route-perform-${c.id}`}>
            <CheckCircle2 className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Utfør rens</span>
          </Button>
        )}
      </div>
    </CardContent>
  </Card>
);

export const DayRoutePanel = ({ routes, routeDate, setRouteDate, routeUserId, setRouteUserId, users, isAdmin, currentUser, customers, onSave, onPerform }) => {
  const route = routes.find((r) => r.user_id === routeUserId);
  const stops = route?.customers || [];
  const ids = stops.map((c) => c.id);
  const others = routes.filter((r) => r.user_id !== routeUserId && r.customers?.length);
  const move = (i, d) => { const n = [...ids]; [n[i], n[i + d]] = [n[i + d], n[i]]; onSave(n); };

  return (
    <div className="space-y-4" data-testid="day-route-panel">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <Label>Dato</Label>
          <Input type="date" value={routeDate} onChange={(e) => e.target.value && setRouteDate(e.target.value)} data-testid="route-date-input" />
        </div>
        <div className="space-y-1.5">
          <Label>Rute for</Label>
          {isAdmin ? (
            <Select value={routeUserId} onValueChange={setRouteUserId}>
              <SelectTrigger data-testid="route-user-select"><SelectValue placeholder="Velg bruker" /></SelectTrigger>
              <SelectContent>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}{u.id === currentUser?.id ? ' (meg)' : ''}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <div className="h-10 px-3 flex items-center rounded-md border bg-slate-50 text-sm" data-testid="route-user-self">{currentUser?.name}</div>
          )}
        </div>
        <div className="space-y-1.5">
          <Label>Legg til kunde</Label>
          <CustomerCombobox customers={customers} value="" excludeIds={ids} onChange={(id) => onSave([...ids, id])} placeholder="Søk og legg til..." testId="route-add-customer" />
        </div>
      </div>

      {stops.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-slate-500">
          <Route className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          Ingen kunder i ruten. Bruk «Legg i rute» på kundene under Forfalt / Denne uken / Kommende, eller søk over.
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          <p className="text-sm text-slate-500" data-testid="route-summary">{stops.length} stopp · {stops.filter((s) => s.done).length} renset</p>
          {stops.map((c, i) => (
            <RouteStop key={c.id} c={c} index={i} total={stops.length} onMove={move} onRemove={(id) => onSave(ids.filter((x) => x !== id))} onPerform={onPerform} />
          ))}
        </div>
      )}

      {others.length > 0 && (
        <div className="pt-2" data-testid="route-others">
          <p className="text-xs font-medium text-slate-500 mb-2">Andre ruter denne dagen</p>
          <div className="flex flex-wrap gap-2">
            {others.map((r) => (
              <button key={r.user_id} type="button" onClick={() => isAdmin && setRouteUserId(r.user_id)} className="px-3 py-1 text-xs rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200" data-testid={`route-other-${r.user_id}`}>
                {r.user_name}: {r.customers.length} stopp
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DayRoutePanel;
